import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { deleteAuth } from '../../../util/Auth';
import { getLocalStore } from '../../../util/Helpers';
import { USER_UUID } from '../../../util/Constants';
import { getUser } from '../../../services/UserService';
import { recordActivity } from '../../../services/ActivityService';
import './NavigationMenu.css';

const getDisplayName = (user) => {
  const firstName = user?.firstName || user?.first_name;
  const lastName = user?.lastName || user?.last_name;
  const explicitName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (explicitName) return explicitName;
  return String(user?.name || '').trim() || 'User';
};

const getInitials = (name) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0).toUpperCase())
  .join('') || 'U';

const NavigationMenu = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-solid fa-chart-line', path: '/' },
    { id: 'models', label: 'Models', icon: 'fa-solid fa-user', path: '/models' },
    { id: 'fantasy', label: 'Fantasy Requests', icon: 'fa-solid fa-star', path: '/fantasy' },
    { id: 'casting', label: 'Casting', icon: 'fa-solid fa-video', path: '/casting' },
    { id: 'contact', label: 'Contact', icon: 'fa-solid fa-address-book', path: '/contact' },
    { id: 'users', label: 'Users', icon: 'fa-solid fa-users', path: '/users' },
    { id: 'trash', label: 'Trash Bin', icon: 'fa-solid fa-trash-can', path: '/trash' },
  ];

  useEffect(() => {
    const userUuid = getLocalStore(USER_UUID);
    if (!userUuid) return undefined;

    let isMounted = true;
    getUser(userUuid).then(user => {
      if (isMounted) setCurrentUser(user);
    }).catch(() => {
      // Keep the neutral fallback when the user profile cannot be loaded.
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const displayName = getDisplayName(currentUser);
  const initials = getInitials(displayName);

  const handleLogout = () => {
    recordActivity({ type: 'auth', action: 'Logged out' });
    deleteAuth();
    navigate('/login');
    setIsOpen(false);
  };

  return (
    <>
      <nav className={`navigation-menu ${isOpen ? 'is-open' : ''}`} aria-label="Main navigation">
        <div className="menu-header">
          <div>
            <div className="menu-logo">SADIDA</div>
            <div className="menu-admin">Admin</div>
          </div>
          <button
            type="button"
            className="menu-toggle"
            aria-expanded={isOpen}
            aria-controls="main-menu-items"
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setIsOpen(current => !current)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      
        <ul id="main-menu-items" className="menu-items">
          {menuItems.map(item => (
            <li key={item.id} className="menu-item">
              <NavLink 
                to={item.path} 
                className={({ isActive }) => `menu-button ${isActive ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                <i className={`menu-icon ${item.icon}`}></i>
                <span className="menu-label">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="menu-footer">
          <div className="user-info">
            <div className="user-left">
              <div className="user-avatar" aria-hidden="true">{initials}</div>
              <div className="user-details">
                <span className="user-name">{displayName}</span>
                <span className="user-role">Administrator</span>
              </div>
            </div>
            <button className="logout-icon" onClick={handleLogout} title="Logout" aria-label="Logout">
              <i className="fa-solid fa-arrow-right-from-bracket"></i>
            </button>
          </div>
        </div>
      </nav>
      {isOpen && <button type="button" className="menu-backdrop" aria-label="Close navigation menu" onClick={() => setIsOpen(false)} />}
    </>
  );
};

export default NavigationMenu;